import { useFormik } from "formik";
import { useEffect, useState, useMemo } from "react";
import { FormContainer } from "../../ManagementPortal/Shared/FormContainer";
import * as Components from "../../../components";
import {
  useGetVehicleGroupsQuery,
  useGetDeviceGroupsQuery,
  useCreateReportMutation,
  useGetVehiclesQuery,
  useGetDevicesQuery,
  useGetReportConfigurationsQuery,
  useGetTimezoneQuery,
  useGetEventConfigurationsQuery,
  useGetEventTypesQuery,
} from "../../ManagementPortal/services";
import moment from "moment";
import { useDropDownSearch, useForm } from "../../../hooks";
import { reportFormValidation } from "../validation";
import {
  selectionType,
  selectionTypeNoGroup,
  assetTypes,
  assetTypesNoDevice,
  fileFormats,
  subTypes,
  subTypesNoSummary,
  summaryIntervals,
} from "./static";
import { FMDPStorage } from "../../../shared/helper";
import { reportTypes } from "../../../shared/reportTypes";
import { getLocalizedString } from "../../../shared/translation";
import { applicableDates } from "../../../shared/dropdownLists";
import { hasAdminRole as checkAdminRole, getUserIdForFiltering } from "../utils";

export const ReportsForm = ({ onClose }) => {
  const [initialValues, setInitalValues] = useState({
    name: "",
    metadata: {
      selection_criteria: "",
      asset_ids: [],
      asset_type: "",
      device_imeis: [],
      plate_numbers: [],
    },
    start_time: null,
    end_time: null,
    report_configuration_id: "",
    file_format: ["csv"],
    time_zone_id: 9,
    event_type: "",
    event_type_id: "",
    event_configuration_id: "",
    report_type: "trackpoint",
    report_sub_type: "",
    summary_interval: "",
    group_by_asset: false,
    applicable_days_type: "all_days",
    last_active_at_threshold: null,
  });
  const [resetCount, setResetCount] = useState(0);
  const [selectedRange, setSelectedRange] = useState({
    startDate: moment().subtract(24, "hours"),
    endDate: moment(),
  });

  const [selectedDate, setSelectedDate] = useState({
    startDate: moment().subtract(24, "hours"),
  });

  // initialize Formik
  const formik = useFormik({
    initialValues,
    validationSchema: reportFormValidation,
    enableReinitialize: true,
    onSubmit: (values) => handleFormSubmit(values),
    context: {
      report_type: initialValues.report_type,
      event_type: initialValues.event_type,
    },
  });

  const currentUser = FMDPStorage.get("current-user");
  const selectedAppId = FMDPStorage.get("selected-app-id");
  const hasAdminRole = checkAdminRole(currentUser, selectedAppId);

  // State for custom event configuration search
  const [customEventSearchKey, setCustomEventSearchKey] = useState("");

  // Fetch all event types from API for the Event Type dropdown
  const { data: allEventTypesData } = useGetEventTypesQuery(
    { per_page: 100 },
    { skip: formik.values.report_type !== "event" }
  );

  // Check if selected event type is custom classification
  const selectedEventType = useMemo(() => {
    return allEventTypesData?.data?.find(
      (et) => String(et.id) === String(formik.values.event_type_id)
    );
  }, [allEventTypesData, formik.values.event_type_id]);

  const isCustomEventType = selectedEventType?.classification === "custom";

  // Fetch event configurations for selected custom event type
  const customEventQuery = useMemo(() => {
    const queryObj = {
      event_type_id_in: [Number(formik.values.event_type_id)],
      ...(customEventSearchKey && { name_cont: customEventSearchKey }),
    };
    return JSON.stringify(queryObj);
  }, [formik.values.event_type_id, customEventSearchKey]);

  const { data: customEventConfigurations, isFetching: isCustomEventsFetching } =
    useGetEventConfigurationsQuery(
      { per_page: 600, page: 1, q: customEventQuery },
      { skip: !isCustomEventType || !formik.values.event_type_id }
    );

  const getReportTypeId = (type) => {
    let id = null;
    reportTypes?.map((report, index) => {
      if (report?.id === type) {
        id = index;
      }
    });
    return id;
  };

  //Get Reports Configuration
  const { data: reportsConfiguration } = useGetReportConfigurationsQuery({
    per_page: 1000,
    q: JSON.stringify({ report_type_eq: getReportTypeId(formik?.values?.report_type) }),
  });

  const getUserId = () => {
    return getUserIdForFiltering(currentUser, selectedAppId);
  };

  //Get Vehicle Groups
  const { data: vehicleData, setSearchKey: vehicleDataSerch } = useDropDownSearch({
    useData: useGetVehicleGroupsQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.metadata.asset_ids,
    additionalParams: { per_page: 600 },
  });

  //Get Device Groups
  const { data: deviceData, setSearchKey: deviceDataSerch } = useDropDownSearch({
    useData: useGetDeviceGroupsQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.metadata.asset_ids,
    additionalParams: { per_page: 600 },
  });

  //Get VehicleQuery
  const { data: vehicleListData, setSearchKey: vehicleListDataSerch } = useDropDownSearch({
    useData: useGetVehiclesQuery,
    simpleSearchKey: "name_or_plate_number_cont",
    selectedIds: formik.values.metadata.asset_ids,
    additionalParams: { per_page: 600, filter_user_id: getUserId() },
  });

  //Get DeviceQuery
  const { data: deviceListData, setSearchKey: deviceListDataSearch } = useDropDownSearch({
    useData: useGetDevicesQuery,
    simpleSearchKey: "device_id_cont",
    additionalSearchKey: ["name_cont"],
    selectedIds: formik.values.metadata.asset_ids,
    additionalParams: { per_page: 600 },
  });

  const { create: createReportConfiguration, update: updateReportConfiguration } = useForm({
    createMutation: useCreateReportMutation,
    updateMutation: useCreateReportMutation,
    closeForm: onClose,
    setError: formik.setErrors,
  });

  const { data: timeZones } = useGetTimezoneQuery({});

  //Handle Date Range Change
  useEffect(() => {
    formik.setFieldValue("start_time", moment(selectedRange.startDate).unix());
    formik.setFieldValue("end_time", moment(selectedRange.endDate).unix());
    setResetCount((i) => i + 1);
  }, [selectedRange]);

  //Handle Date Change
  useEffect(() => {
    formik.setFieldValue("last_active_at_threshold", moment(selectedDate.startDate).unix());
    // setResetCount((i) => i + 1);
  }, [selectedDate]);

  const handleFormSubmit = (formdata) => {
    const submitData = { ...formdata };

    // Ensure event_type_id is integer for event reports
    if (submitData.report_type === "event" && submitData.event_type_id) {
      submitData.event_type_id = Number(submitData.event_type_id);
    }
    // Ensure event_configuration_id is integer if present
    if (submitData.event_configuration_id) {
      submitData.event_configuration_id = Number(submitData.event_configuration_id);
    }

    // file_format is an array (e.g. ["csv", "pdf"]) - send as comma-separated string
    if (Array.isArray(submitData.file_format)) {
      submitData.file_format = submitData.file_format.join(",");
    }

    createReportConfiguration({ formData: submitData });
  };

  const modifiedVehicleData = vehicleListData?.data?.map((item) => {
    return {
      name: item?.plate_number,
      id: item?.id,
      plate_number: item?.plate_number,
    };
  });

  const modifiedDeviceData = deviceListData?.data?.map((item) => {
    return {
      name: item?.device_id,
      id: item?.id,
      plate_number: item?.plate_number,
    };
  });

  // For report type anything other than trackpoint, report configuration is not present hence clearing it
  const handleReportType = (value) => {
    formik.setFieldValue("report_type", value);
    if (value !== "trackpoint") {
      formik.setFieldValue("report_configuration_id", "");
    }
    formik.setFieldValue("report_sub_type", "");
    formik.setFieldValue("summary_interval", "");

    // Clear event type and custom event configurations when switching away from event type
    if (value !== "event") {
      formik.setFieldValue("event_type", "");
      formik.setFieldValue("event_type_id", "");
      formik.setFieldValue("event_configuration_id", "");
    }

    // If report type is offline report, clear date range and applicable days
    if (value === "offline_report") {
      formik.setFieldValue("start_time", null);
      formik.setFieldValue("end_time", null);
      formik.setFieldValue("applicable_days_type", null);
      setSelectedDate({
        startDate: moment().subtract(24, "hours"),
      });
    } else {
      setSelectedRange({
        startDate: moment().subtract(24, "hours"),
        endDate: moment(),
      });
      formik.setFieldValue("applicable_days_type", "all_days");
      formik.setFieldValue("last_active_at_threshold", null);
    }
  };

  const getWarningMessage = () => {
    const diffInDays =
      (formik?.values?.end_time * 1000 - formik?.values?.start_time * 1000) / 86400000;

    if (formik?.values?.report_sub_type === "summary") {
      if (formik?.values?.summary_interval === "weekly") {
        if (diffInDays < 7) {
          return "You have chosen less than 7 days for weekly report";
        }
      }
      if (formik?.values?.summary_interval === "monthly") {
        if (diffInDays < 28) {
          return "You have chosen less than 28 days for monthly report";
        }
      }
      if (formik?.values?.summary_interval === "daily") {
        if (diffInDays < 1) {
          return "You have chosen less than 24 hours for daily report";
        }
      }
    } else {
      return "";
    }
  };

  return (
    <FormContainer
      customResourceName={getLocalizedString("report", "Report")}
      handleFormSubmit={formik.handleSubmit}
      closeForm={onClose}
      ifTrackingApp={true}
    >
      <Components.QIInput
        label={getLocalizedString("name", "Name")}
        {...formik.getFieldProps("name")}
        error={formik.touched.name && formik.errors.name}
      />

      {/* Report Type */}
      <Components.QICustomSelect
        label={getLocalizedString("type", "Type")}
        value={formik?.values?.report_type}
        onChange={(value) => handleReportType(value)}
      >
        {reportTypes?.map((type, index) => (
          <li value={type.id} key={index}>
            {getLocalizedString(type?.localization_key) || type?.name}
          </li>
        ))}
      </Components.QICustomSelect>

      {/* Report SubType */}
      <Components.QICustomSelect
        label={getLocalizedString("subtype", "Sub-Type")}
        value={formik?.values?.report_sub_type}
        onChange={(value) => {
          formik.setFieldValue("report_sub_type", value);
        }}
        error={formik?.touched?.report_sub_type && formik?.errors?.report_sub_type}
      >
        {(formik?.values?.report_type == "raw" || formik?.values?.report_type == "offline_report"
          ? subTypesNoSummary
          : subTypes
        )?.map((type, index) => (
          <li value={type.id} key={index}>
            {getLocalizedString(type?.localization_key) || type?.name}
          </li>
        ))}
      </Components.QICustomSelect>

      {/* Report Summary Interval */}
      {formik?.values?.report_sub_type === "summary" && (
        <Components.QICustomSelect
          label={getLocalizedString("summary_interval", "Summary Interval")}
          value={formik?.values?.summary_interval}
          onChange={(value) => {
            formik.setFieldValue("summary_interval", value);
          }}
          error={formik?.touched?.summary_interval && formik?.errors?.summary_interval}
        >
          {summaryIntervals?.map((type, index) => (
            <li value={type.id} key={index}>
              {getLocalizedString(type?.localization_key) || type?.name}
            </li>
          ))}
        </Components.QICustomSelect>
      )}

      {/* Group By Asset */}
      {formik?.values?.report_sub_type === "summary" && (
        <Components.QISwitch
          label={getLocalizedString("group_by_asset", "Group By Asset")}
          value={formik.values.group_by_asset}
          onChange={() => formik.setFieldValue("group_by_asset", !formik.values.group_by_asset)}
        />
      )}

      {/* Event Type */}
      {formik?.values?.report_type == "event" && (
        <Components.QICustomSelect
          label={getLocalizedString("event_type", "Event Type")}
          value={formik.values.event_type_id}
          onChange={(value) => {
            const selectedType = allEventTypesData?.data?.find(
              (et) => String(et.id) === String(value)
            );
            formik.setFieldValue("event_type_id", value);
            formik.setFieldValue("event_type", selectedType?.name || "");
            formik.setFieldValue("event_configuration_id", "");
          }}
          error={formik?.touched?.event_type && formik?.errors?.event_type}
        >
          <li value="">{getLocalizedString("select", "Select")}</li>
          {allEventTypesData?.data?.map((et) => (
            <li value={et.id} key={et.id}>
              {et.name.replace(/\b\w/g, (c) => c.toUpperCase())} ({et.classification})
            </li>
          ))}
        </Components.QICustomSelect>
      )}

      {/* Custom Event Configuration (single select) */}
      {formik?.values?.report_type == "event" && isCustomEventType && (
        <>
          {!isCustomEventsFetching && (!customEventConfigurations?.data || customEventConfigurations.data.length === 0) ? (
            <div className="qi-form-element">
              <label className="qi-input_label">
                {getLocalizedString("event_configuration", "Event Configuration")}
              </label>
              <div className="no-custom-events-message">
                {getLocalizedString("no_event_configurations_available", "No event configurations available for this event type")}
              </div>
            </div>
          ) : (
            <Components.QICustomSelect
              label={getLocalizedString("event_configuration", "Event Configuration")}
              value={formik.values.event_configuration_id}
              onChange={(value) => formik.setFieldValue("event_configuration_id", value)}
              onSearch={(value) => setCustomEventSearchKey(value)}
            >
              <li value="">{getLocalizedString("select", "Select")}</li>
              {customEventConfigurations?.data?.map((config) => (
                <li value={config.id} key={config.id}>
                  {config.name}
                </li>
              ))}
            </Components.QICustomSelect>
          )}
        </>
      )}

      {/* Report Configuration */}
      {formik?.values?.report_type == "trackpoint" &&
        formik?.values?.report_sub_type == "detailed" && (
          <Components.QICustomSelect
            label={getLocalizedString("report_configuration", "Report Configuration")}
            value={formik.values.report_configuration_id}
            onChange={(value) => {
              formik.setFieldValue("report_configuration_id", Number(value));
            }}
            error={
              formik?.touched?.report_configuration_id && formik?.errors?.report_configuration_id
            }
            labelClassName="report-configuration"
          >
            {reportsConfiguration?.data?.length ? (
              reportsConfiguration?.data?.map((item, id) => {
                return (
                  <li value={item?.id} id={id}>
                    {item?.name}
                  </li>
                );
              })
            ) : (
              <li value={null}>No Data Found</li>
            )}
          </Components.QICustomSelect>
        )}

      {/* Selection Criteria */}
      <Components.QICustomSelect
        label={getLocalizedString("selected_criteria", "Selected Criteria")}
        value={formik.values.metadata.selection_criteria}
        error={
          formik?.touched?.metadata?.selection_criteria &&
          formik?.errors?.metadata?.selection_criteria
        }
        onChange={(value) => {
          formik.setFieldValue("metadata.selection_criteria", value);
        }}
      >
        {(!hasAdminRole ? selectionTypeNoGroup : selectionType)?.map((item, index) => {
          return (
            <li value={item?.name} key={index}>
              {getLocalizedString(item?.localization_key) || item?.name}
            </li>
          );
        })}
      </Components.QICustomSelect>

      {/* Selected Assets Type */}
      <Components.QICustomSelect
        label={getLocalizedString("asset_type", "Asset Type")}
        value={formik.values.metadata.asset_type}
        error={formik?.touched?.metadata?.asset_type && formik?.errors?.metadata?.asset_type}
        onChange={(value) => {
          formik.setFieldValue("metadata.asset_type", value);
        }}
      >
        {(hasAdminRole ? assetTypes : assetTypesNoDevice)?.map((item, index) => {
          return (
            <li value={item?.name} key={index}>
              {getLocalizedString(item?.localization_key) || item?.name}
            </li>
          );
        })}
      </Components.QICustomSelect>

      {/* Selected Assets Report */}
      {formik.values.metadata.asset_type === "Vehicle" &&
        formik.values.metadata.selection_criteria === "Group" && (
          <Components.QIMultiSelectDropDown
            label={getLocalizedString("vehicles_group", "Vehicles Group")}
            data={vehicleData?.data || []}
            selected={formik.values.metadata.asset_ids}
            error={formik?.touched?.metadata?.asset_ids && formik?.errors?.metadata?.asset_ids}
            onChange={(value) => formik.setFieldValue("metadata.asset_ids", value)}
            onSearch={(value) => vehicleDataSerch(value)}
          />
        )}

      {formik.values.metadata.asset_type === "Device" &&
        formik.values.metadata.selection_criteria === "Group" && (
          <Components.QIMultiSelectDropDown
            label={getLocalizedString("devices_group", "Devices Group")}
            data={deviceData?.data || []}
            selected={formik.values.metadata.asset_ids}
            error={formik?.touched?.metadata?.asset_ids && formik?.errors?.metadata?.asset_ids}
            onChange={(value) => formik.setFieldValue("metadata.asset_ids", value)}
            onSearch={(value) => deviceDataSerch(value)}
          />
        )}

      {/* Vehicle List Reports */}
      {formik.values.metadata.selection_criteria === "Asset" &&
        formik.values.metadata.asset_type === "Vehicle" && (
          <Components.QIMultiSelectDropDown
            label="Vehicles"
            data={modifiedVehicleData || []}
            selected={formik.values.metadata.asset_ids}
            error={formik?.touched?.metadata?.asset_ids && formik?.errors?.metadata?.asset_ids}
            onChange={(value) => {
              formik.setFieldValue("metadata.asset_ids", value);
              const newData = modifiedVehicleData
                ?.filter((val) => {
                  return value?.includes(val?.id);
                })
                .map((val) => {
                  return val?.plate_number;
                });
              formik.setFieldValue("metadata.plate_numbers", newData);
            }}
            onSearch={(value) => vehicleListDataSerch(value)}
          />
        )}

      {/* Device List Reports */}
      {formik.values.metadata.selection_criteria === "Asset" &&
        formik.values.metadata.asset_type === "Device" && (
          <Components.QIMultiSelectDropDown
            label="Devices"
            data={modifiedDeviceData || []}
            selected={formik.values.metadata.asset_ids}
            error={formik?.touched?.metadata?.asset_ids && formik?.errors?.metadata?.asset_ids}
            onChange={(value) => {
              formik.setFieldValue("metadata.asset_ids", value);
              const newData = modifiedDeviceData
                ?.filter((val) => {
                  return value?.includes(val?.id);
                })
                .map((val) => {
                  return val?.plate_number;
                });
              formik.setFieldValue("metadata.plate_numbers", newData);
            }}
            onSearch={(value) => deviceListDataSearch(value)}
          />
        )}

      {/* File Format */}
      <Components.QIMultiSelectDropDown
        label={getLocalizedString("file_format", "File Format")}
        data={fileFormats}
        selected={formik.values.file_format}
        onChange={(value) => {
          formik.setFieldValue("file_format", value);
        }}
        className="narrow-multiselect-dropdown"
      />

      {/* Date Range */}
      {formik.values.report_type === "offline_report" ? (
        <Components.QICustomDateRangePicker
          label={getLocalizedString("last_active_threshold", "Last Active Threshold")}
          selectedRange={selectedDate}
          localRange={true}
          setSelectedRangeLocal={setSelectedDate}
          className={"report-form-datepicker"}
          singleDatePicker={true}
          showDefaultRanges={false}
        />
      ) : (
        <Components.QICustomDateRangePicker
          label={getLocalizedString("date_&_time", "Date & Time")}
          resetCount={resetCount}
          selectedRange={selectedRange}
          setSelectedRangeLocal={setSelectedRange}
          localReport={true}
          setResetCount={setResetCount}
          className={"report-form-datepicker"}
          warningMessage={getWarningMessage()}
        />
      )}

      {formik.values.report_type !== "offline_report" && (
        <Components.QICustomSelect
          label={getLocalizedString("applicable_days", "Applicable Days")}
          value={formik.values.applicable_days_type}
          onChange={(value) => {
            formik.setFieldValue("applicable_days_type", value);
          }}
        >
          {applicableDates?.map((item, index) => {
            return (
              <li value={item?.id} key={index}>
                {getLocalizedString(item?.localization_key) || item?.name}
              </li>
            );
          })}
        </Components.QICustomSelect>
      )}

      {/* Time Zone */}
      <Components.QICustomSelect
        label={getLocalizedString("timezone", "Timezone")}
        value={formik.values.time_zone_id}
        onChange={(value) => {
          formik.setFieldValue("time_zone_id", Number(value));
        }}
        className="timezone-dropdown"
      >
        {timeZones?.data?.map((item) => {
          return <li value={item?.id}>{item?.name}</li>;
        })}
      </Components.QICustomSelect>
    </FormContainer>
  );
};
